// components/Login.jsx - Login and Registration with Organization Invite Code
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Eye, EyeOff, Building2, CheckCircle, XCircle } from 'lucide-react';
import chartiqLogoLight from '../chartiq-logo-light.png';
import chartiqLogoDark from '../chartiq-logo.png';

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

const Login = () => {
    const navigate = useNavigate();
    const { login, register, error } = useAuth();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [inviteStatus, setInviteStatus] = useState(null); // null, 'checking', 'valid', 'invalid'
    const [inviteOrgName, setInviteOrgName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [localError, setLocalError] = useState('');

    const [theme] = useState(localStorage.getItem('chartiq-theme') || 'light');
    const logoSrc = theme === 'light' ? chartiqLogoDark : chartiqLogoLight;

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // Debounced invite code validation
    const validateInviteCode = useCallback(async (code) => {
        if (!code || code.trim().length < 4) {
            setInviteStatus(null);
            setInviteOrgName('');
            return;
        }

        setInviteStatus('checking');
        try {
            const res = await fetch(`${API_BASE}/auth/validate-invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ invite_code: code })
            });
            const data = await res.json();
            if (data.valid) {
                setInviteStatus('valid');
                setInviteOrgName(data.org_name);
            } else {
                setInviteStatus('invalid');
                setInviteOrgName('');
            }
        } catch {
            setInviteStatus('invalid');
            setInviteOrgName('');
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (inviteCode.trim().length >= 4) {
                validateInviteCode(inviteCode);
            } else {
                setInviteStatus(null);
                setInviteOrgName('');
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [inviteCode, validateInviteCode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setIsLoading(true);

        let result;
        if (isLogin) {
            result = await login(email, password);
        } else {
            // Validate invite code (Required)
            if (!inviteCode.trim()) {
                setLocalError('Organization Invite Code is required.');
                setIsLoading(false);
                return;
            }
            if (inviteStatus !== 'valid') {
                setLocalError('Please enter a valid Organization Invite Code.');
                setIsLoading(false);
                return;
            }
            result = await register(email, password, name, inviteCode.trim());
        }

        if (!result.success) {
            setLocalError(result.error);
        } else {
            sessionStorage.setItem('just_logged_in', 'true');
            navigate('/', { replace: true });
        }

        setIsLoading(false);
    };

    const toggleMode = () => {
        setIsLogin(!isLogin);
        setLocalError('');
        setEmail('');
        setPassword('');
        setName('');
        setInviteCode('');
        setInviteStatus(null);
        setInviteOrgName('');
    };

    return (
        <div className="login-container-animated">
            {/* Animated Background */}
            <div className="login-background">
                <div className="login-gradient"></div>
            </div>

            {/* Floating Split Card */}
            <div className="login-card-floating">
                {/* Left Side - Brand Panel (Compact) */}
                <div className="floating-brand-panel">
                    <div className="brand-content-compact">
                        <div className="brand-logo-compact">
                            <img src={logoSrc} alt="ChartIQ Logo" className="chartiq-logo" style={{ height: '60px', width: 'auto' }} />
                        </div>
                        <h3>AI Powered Data Visualisation</h3>
                        <p>Connect with your data and let AI create powerful visualizations in seconds. Enterprise-grade analytics made simple.</p>
                    </div>
                    <div className="brand-footer-compact">
                        <p>© 2026 SaturnIQ Intelligent Solutions</p>
                    </div>
                </div>

                {/* Right Side - Auth Panel */}
                <div className="floating-auth-panel">
                    <div className="auth-header-compact">
                        <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
                        <p>{isLogin ? 'Sign in to continue' : 'Get started for free'}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form-compact">
                        {!isLogin && (
                            <>
                                {/* Name */}
                                <div className="form-group compact" style={{ marginBottom: '16px' }}>
                                    <label htmlFor="name">Full Name</label>
                                    <div className="input-group">
                                        <User size={16} className="input-icon" />
                                        <input
                                            id="name"
                                            type="text"
                                            placeholder="John Doe"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            autoComplete="name"
                                        />
                                    </div>
                                </div>

                                {/* Invite Code */}
                                <div className="form-group compact" style={{ marginBottom: '16px' }}>
                                    <label htmlFor="invite-code" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Building2 size={14} />
                                        Organization Invite Code
                                        <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>
                                    </label>
                                    <div className="input-group" style={{ position: 'relative' }}>
                                        <Building2 size={16} className="input-icon" />
                                        <input
                                            id="invite-code"
                                            type="text"
                                            placeholder="e.g. MSFT-4A2B-C8D1"
                                            value={inviteCode}
                                            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                            autoComplete="off"
                                            required
                                            style={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px',
                                                paddingRight: '40px'
                                            }}
                                        />
                                        {inviteStatus === 'checking' && (
                                            <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                                                <span className="loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                                            </span>
                                        )}
                                        {inviteStatus === 'valid' && (
                                            <CheckCircle size={18} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981' }} />
                                        )}
                                        {inviteStatus === 'invalid' && (
                                            <XCircle size={18} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444' }} />
                                        )}
                                    </div>
                                    {inviteStatus === 'valid' && inviteOrgName && (
                                        <div style={{
                                            marginTop: '6px',
                                            fontSize: '0.78rem',
                                            color: '#10b981',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            background: 'rgba(16, 185, 129, 0.08)',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(16, 185, 129, 0.2)'
                                        }}>
                                            <CheckCircle size={12} />
                                            You'll join <strong style={{ marginLeft: '2px' }}>{inviteOrgName}</strong>
                                        </div>
                                    )}
                                    {inviteStatus === 'invalid' && (
                                        <div style={{
                                            marginTop: '6px',
                                            fontSize: '0.78rem',
                                            color: '#ef4444',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <XCircle size={12} />
                                            Invalid invite code
                                        </div>
                                    )}
                                    {!inviteCode.trim() && (
                                        <div style={{
                                            marginTop: '4px',
                                            fontSize: '0.72rem',
                                            color: 'var(--text-muted)'
                                        }}>
                                            Enter the code provided by your organization admin to join.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="form-group compact">
                            <label htmlFor="email">Email</label>
                            <div className="input-group">
                                <Mail size={16} className="input-icon" />
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="name@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className="form-group compact">
                            <label htmlFor="password">Password</label>
                            <div className="input-group">
                                <Lock size={16} className="input-icon" />
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {(localError || error) && (
                            <div className="error-message compact">
                                {localError || error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="login-button compact"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="loading-spinner"></span>
                            ) : (
                                isLogin ? 'Sign In' : 'Sign Up'
                            )}
                        </button>
                    </form>

                    <div className="login-footer-compact">
                        <p>
                            {isLogin ? "New here?" : 'Have an account?'}
                            <button
                                type="button"
                                className="toggle-mode link"
                                onClick={toggleMode}
                            >
                                {isLogin ? 'Create Account' : 'Log In'}
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
